/**
 * 시공사례 카드 한 장. 목록 그리드와 드래그 정렬이 같은 것을 쓴다.
 *
 * 이미지는 손으로 넣은 바깥 주소라 도메인을 미리 알 수 없다 — `next/image` 는
 * `remotePatterns` 를 요구하므로 여기서는 쓸 수 없고, 평범한 `img` 를 쓴다.
 */

import { ImageOff } from "lucide-react";
import { Link } from "@/lib/nav";
import { ReactNode } from "react";

import SectionCard from "@buildpy/ui/section-card";
import StatusBadge from "@buildpy/ui/status-badge";
import { cn } from "@/lib/utils";
import {
  AREA_BAND_LABEL,
  AreaBand,
  RESIDENCE_LABEL,
  Residence,
} from "@buildpy/core/modules/customer/types";

export interface PortfolioCardRow {
  id: string;
  title: string;
  summary: string;
  cover_url: string;
  area_band: AreaBand | null;
  residence: Residence | null;
  published: boolean;
  image_count: number;
}

interface Props {
  row: PortfolioCardRow;
  /** 드래그 핸들. 정렬이 꺼져 있으면 넣지 않는다. */
  handle?: ReactNode;
  className?: string;
}

/** 평형대·거주형태는 안 고를 수 있다. 둘 다 없으면 줄 자체를 빼서 카드가 들쭉날쭉하지 않게. */
const traits = (row: PortfolioCardRow): string[] =>
  [
    row.area_band ? AREA_BAND_LABEL[row.area_band] : null,
    row.residence ? RESIDENCE_LABEL[row.residence] : null,
  ].filter((value): value is string => value !== null);

const PortfolioCard = ({ row, handle, className }: Props) => {
  const meta = traits(row);

  return (
    <SectionCard
      as="article"
      className={cn("group relative flex flex-col overflow-hidden", className)}
      padding="none">
      <div className="relative aspect-[4/3] w-full bg-muted">
        {row.cover_url ? (
          <img
            alt={row.title}
            className="size-full object-cover"
            loading="lazy"
            src={row.cover_url}
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageOff className="size-6" />
          </div>
        )}

        {handle}

        <div className="absolute bottom-1 right-1 rounded-md bg-overlay/60 px-1.5 py-0.5 text-xs text-overlay-foreground">
          {row.image_count}장
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-y-1.5 p-3">
        <div className="flex items-start justify-between gap-x-2">
          <Link
            className="line-clamp-2 text-sm font-medium hover:underline"
            href={`/app/portfolio/${row.id}`}>
            {row.title}
          </Link>
          <StatusBadge className="shrink-0" label={row.published ? "공개" : "비공개"} tone={row.published ? "done" : "hold"} />
        </div>

        {meta.length > 0 && (
          <p className="text-xs text-muted-foreground">{meta.join(" · ")}</p>
        )}

        {row.summary && (
          <p className="line-clamp-2 text-xs text-muted-foreground">
            {row.summary}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default PortfolioCard;
